// Custom laser-engraved Christmas ornaments: a demand-test page under the
// owner's traffic-first policy (.seo/brand.md, 2026-11-01). Prices are the
// published laser engraving terms (lib/laser-engraving-facts.js); the ornament
// blank itself is "quoted per project". Images are AI-generated mockups and
// captioned as such. Keyword targets: OpenSEO US 2026-11-02 (see
// .seo/content-ledger.md). The season peaks in November and December.
const {
  laserEngravingFacts: engraving,
} = require('../../lib/laser-engraving-facts')

const quoted = 'Quoted per project'
const smallItems = engraving.productionRanges[0].price

const christmasOrnaments = {
  path: '/services/laser-engraving/christmas-ornaments/',
  reviewedDate: '2026-11-02',
  reviewedDateLabel: 'November 2, 2026',
  title: 'Personalized & Custom Christmas Ornaments | PrintAWorld',
  description:
    'Custom laser-engraved Christmas ornaments in wood, acrylic and metal: names for families, logos in bulk for client and employee gifts. Made in NYC.',
  eyebrow: 'Custom ornaments / laser engraved in New York',
  heading:
    'Personalized Christmas ornaments, and custom logo ornaments in bulk.',
  answer: `PrintAWorld laser-cuts and engraves custom Christmas ornaments in New York: one ornament with a family name, or a run of 50 to 500 with your company logo for client and employee gifts. We make them in birch or maple wood, clear acrylic or metal, with names, logos and the year engraved, and color added by UV printing if you want it. Engraving is a $${engraving.setupFee} setup plus ${smallItems.replace(' per piece', '')} per ornament; the ornament itself is ${quoted.toLowerCase()}.`,
  cta: 'Get an ornament quote',
}

// AI-generated mockups (Codex image_gen, 2026-11-02), local webp in
// static/images/christmas-ornaments/. Captions say they are AI-generated.
const images = {
  logoBox: {
    file: 'logo-ornaments-box',
    alt: 'Round birch wood ornaments engraved with the same company logo and 2026, in an open black gift box with twine',
    caption: 'Logo ornaments for client gifts',
  },
  familyName: {
    file: 'family-name-ornament',
    alt: 'Laser-cut birch ornament engraved with a family name and the year, hanging on a lit Christmas tree',
    caption: 'A family name and the year',
  },
  acrylic: {
    file: 'acrylic-ornament',
    alt: 'Clear acrylic ornament with a frosted engraved snowflake, a name and a small red printed accent',
    caption: 'Clear acrylic, engraved and printed',
  },
  metal: {
    file: 'metal-ornament',
    alt: 'Brushed brass ornament engraved with a fine geometric pattern, on dark linen beside a pine sprig',
    caption: 'Brass, engraved on the metal laser',
  },
  bulkTable: {
    file: 'bulk-ornaments-table',
    alt: 'Rows of identical engraved wood ornaments laid out on a studio worktable',
    caption: 'A production run, checked piece by piece',
  },
  placeCard: {
    file: 'place-card-ornament',
    alt: 'Engraved wood ornament with a guest name used as a place card at a holiday dinner table',
    caption: 'Place cards guests take home',
  },
}

const uses = [
  {
    label: 'Client gifts',
    title: 'Your logo, for every client',
    body: 'A logo ornament in a gift box, mailed with the holiday card or handed over at the year-end meeting. One design, engraved the same on every piece.',
  },
  {
    label: 'Employee gifts',
    title: 'Every name, on its own ornament',
    body: 'The company logo plus each person’s name, from a spreadsheet you send. Each ornament is engraved for one person.',
  },
  {
    label: 'Holiday parties',
    title: 'Place cards guests keep',
    body: 'Ornaments engraved with each guest’s name that mark the seat at dinner and go home on the tree.',
  },
  {
    label: 'Families',
    title: 'A name and the year',
    body: 'A family surname, a baby’s first Christmas, a couple’s first year, a pet. One ornament is fine.',
  },
  {
    label: 'Shops and brands',
    title: 'Ornaments to sell',
    body: 'A city skyline, a landmark or your brand character as a laser-cut ornament for your store, priced per piece for each quantity.',
  },
]

const materials = [
  {
    label: '01',
    title: 'Birch or maple wood',
    body: 'Warm, light wood that engraves dark brown. Cut to any shape: a circle, a star, a tree, your logo outline.',
  },
  {
    label: '02',
    title: 'Clear or colored acrylic',
    body: 'Engraves frosted white on clear acrylic. Add a color logo by UV printing on the same piece.',
  },
  {
    label: '03',
    title: 'Metal',
    body: 'Brass, stainless steel or anodized aluminum blanks engraved on our MOPA laser. We test your metal first to check the look.',
  },
  {
    label: '04',
    title: 'Your own ornaments',
    body: 'Already have blank ornaments or branded ones? Send them and we engrave them, after a test on one piece.',
  },
]

const steps = [
  {
    title: 'Send your artwork and count',
    body: 'Your logo or the names, the quantity, the material you like and the date you need them.',
  },
  {
    title: 'Approve a proof',
    body: 'We lay out the shape and engraving and send a proof. For runs, we engrave one sample first.',
  },
  {
    title: 'We cut and engrave',
    body: 'Each ornament is laser-cut, engraved and checked, with names matched to your list.',
  },
  {
    title: 'Ribbon, box, ship',
    body: 'We add ribbon or string and pack them, then ship anywhere in the U.S. or arrange NYC pickup.',
  },
]

const priceRows = [
  { label: 'Engraving, small items', value: smallItems },
  { label: 'Ornament blank', value: quoted },
  { label: 'Custom shape or color print', value: quoted },
  { label: 'Ribbon, boxes, gift packing', value: quoted },
  { label: 'Rush', value: 'Case by case' },
]

const faqs = [
  {
    question: 'How much do custom engraved ornaments cost?',
    answer: `Laser engraving is a $${engraving.setupFee} setup per job plus ${smallItems} for small items like ornaments. The ornament blank, a custom shape, color printing and gift boxes are ${quoted.toLowerCase()}. Ask for several quantities and we price each one.`,
  },
  {
    question: 'Can you make custom ornaments in bulk with our logo?',
    answer:
      'Yes. Logo ornaments for client and employee gifts are made the way we engrave other branded items in bulk. Send your logo as a vector file if you have one, the count and the date. We engrave one sample for your approval, then the run.',
  },
  {
    question: 'Can each ornament have a different name?',
    answer:
      'Yes. Send the names in a spreadsheet and we engrave one per ornament, with the logo or design kept the same. We check the names against your list before packing.',
  },
  {
    question: 'Is there a minimum order?',
    answer:
      'No. One personalized ornament is fine. The setup fee is charged once per job, so a larger run costs less per piece.',
  },
  {
    question: 'When should I order ornaments for Christmas?',
    answer: `Standard laser engraving lead time is ${engraving.standardLeadTime}, plus shipping. For Christmas, send your request in November and tell us your in-hand date. Rush jobs are possible case by case.`,
  },
  {
    question: 'What materials can ornaments be made in?',
    answer:
      'Birch or maple wood, clear or colored acrylic, and metal such as brass, stainless steel or anodized aluminum. We can also engrave ornaments you already have, after testing one piece.',
  },
  {
    question: 'Can you add color to an engraved ornament?',
    answer:
      'Yes. We UV print color logos and accents directly on wood and acrylic ornaments, on the same piece as the engraving.',
  },
  {
    question: 'Can you engrave ornaments I already bought?',
    answer:
      'Usually. Send a photo and the material. Glass, ceramic and coated ornaments need a test first, because the result depends on the coating.',
  },
  {
    question: 'Do you ship?',
    answer:
      'Yes, anywhere in the United States, or pickup from our New York studio by appointment.',
  },
]

const quoteChecklist = [
  'Your logo (vector if you have it) or the names',
  'How many ornaments, or several quantities to price',
  'Wood, acrylic or metal, and the shape you want',
  'Your in-hand date and delivery ZIP',
]

module.exports = {
  christmasOrnaments,
  images,
  uses,
  materials,
  steps,
  priceRows,
  faqs,
  quoteChecklist,
}
